import React, { useEffect, useState } from 'react';
import { AlertTriangle, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/AdminService';

const FRAME_COLOR = '#f59e0b'; // amber-500

/**
 * App-wide warning shown when a global admin has switched (via
 * WorkingOrgSwitcher) into an organization they are not a member of.
 *
 * Rendered once at the App root so it appears on every page regardless of
 * layout: an amber frame around the viewport, a banner tab at the top with a
 * "switch back" action, and the org name prefixed to the browser tab title.
 */
const OtherOrgWarning: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, isImpersonating } = useAuth();
  const [switching, setSwitching] = useState(false);

  const active =
    !!currentUser &&
    currentUser.role === 'admin' &&
    !isImpersonating &&
    !!currentUser.is_outside_home_org &&
    currentUser.organization_id != null;

  const orgName = currentUser?.organization_name || `#${currentUser?.organization_id ?? ''}`;
  const homeId = currentUser?.home_organization_id ?? null;
  const homeName = currentUser?.home_organization_name ?? null;

  // Prefix the tab title so it's visible even when several tabs are open.
  useEffect(() => {
    if (!active) return;
    const prefix = `⚠ [${orgName}] `;
    const apply = () => {
      if (!document.title.startsWith(prefix)) {
        document.title = prefix + document.title.replace(/^⚠ \[[^\]]*\] /, '');
      }
    };
    apply();
    const titleEl = document.querySelector('title');
    const observer = titleEl ? new MutationObserver(apply) : null;
    if (titleEl && observer) observer.observe(titleEl, { childList: true });
    return () => {
      observer?.disconnect();
      document.title = document.title.replace(/^⚠ \[[^\]]*\] /, '');
    };
  }, [active, orgName]);

  if (!active) return null;

  const switchBack = async () => {
    setSwitching(true);
    try {
      await adminService.setWorkingOrganization(homeId);
      window.location.assign('/');
    } catch {
      setSwitching(false);
    }
  };

  return (
    <>
      {/* Frame around the whole viewport — purely visual, never blocks clicks. */}
      <div
        aria-hidden
        className="fixed inset-0 z-[9990] pointer-events-none"
        style={{ boxShadow: `inset 0 0 0 3px ${FRAME_COLOR}` }}
      />

      {/* Banner tab hanging from the top edge. */}
      <div
        role="alert"
        className="fixed top-0 left-1/2 -translate-x-1/2 z-[9991] flex items-center gap-2 max-w-[calc(100vw-16px)] px-3 py-1 rounded-b-lg text-xs font-jakarta text-amber-950 shadow-md"
        style={{ backgroundColor: FRAME_COLOR }}
      >
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="truncate">
          {t('admin.workingOrg.warning', {
            defaultValue: 'You are working in {{org}} — not your organization',
            org: orgName,
          })}
        </span>
        <button
          onClick={switchBack}
          disabled={switching}
          className="flex items-center gap-1 flex-shrink-0 bg-amber-950/10 hover:bg-amber-950/20 rounded px-1.5 py-0.5 font-semibold disabled:opacity-60"
        >
          <LogOut className="w-3 h-3" />
          {homeName
            ? t('admin.workingOrg.switchBackTo', { defaultValue: 'Back to {{org}}', org: homeName })
            : t('admin.workingOrg.leave', { defaultValue: 'Leave organization' })}
        </button>
      </div>
    </>
  );
};

export default OtherOrgWarning;
